import React, { useMemo } from 'react';
import { DiscountType } from 'shared/prisma/types';
import { PercentageOutlined, PlusOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { Button, Col, Form, Input, InputNumber, Row, Select, Space } from 'antd';
import RemoveInput from 'components/RemoveInput';
import { getCalculatedOrder } from 'shared/utils/order';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import CurrencyFormat from 'components/CurrencyFormat';

const getDiscountTypeOptions = currency => [{ value: DiscountType.Amount, label: currency }, { value: DiscountType.Percentage, label: <PercentageOutlined /> }];

const LICENSES = gql`
  query ActiveLicenses {
    activeModuleLicenses {
      id
      uniqueTitle
      title
      module
      level
      settings(keys: ["prices"])
    }
    activeLicenses {
      id
      title
      uniqueTitle
    }
    activeCoupons {
      id
      code
      description
      discountAmount
      discountType
    }
  }
`;

const AmountField = ({ value, label, currency }) => (
  <div>
    <b>
      {`${label}: `}
    </b>
    <CurrencyFormat value={value} currency={currency} />
  </div>
);

const OrderForm = ({ form, currency, onSubmit }) => {
  const { data, loading } = useQuery(LICENSES);
  const products = useMemo(() => [
    ...(data?.activeModuleLicenses?.map(l => ({ value: l.id, label: l.uniqueTitle || l.title })) || []),
    ...(data?.activeLicenses?.map(l => ({ value: l.id, label: l.uniqueTitle || l.title })) || []),
  ], [data]);

  const coupons = useMemo(() => data?.activeCoupons?.map(c => ({ value: c.id, label: c.code })) || [], [data]);

  const lineItemColumns = useMemo(() => [{
    dataIndex: 'relationId',
    title: 'Product',
    sorter: false,
    render: (_, field) => (
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item name={[field.name, 'relationId']}><Select options={products} allowClear loading={loading} /></Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={[field.name, 'title']}><Input /></Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name={[field.name, 'description']}><Input /></Form.Item>
        </Col>
      </Row>
    ),
  }, {
    dataIndex: 'price',
    title: 'Price',
    sorter: false,
    render: (_, field) => <Form.Item name={[field.name, 'price']}><InputNumber /></Form.Item>,
  }, {
    dataIndex: 'quantity',
    title: 'Quantity',
    sorter: false,
    render: (_, field) => <Form.Item name={[field.name, 'quantity']}><InputNumber /></Form.Item>,
  }, {
    dataIndex: 'discount',
    title: 'Discount',
    sorter: false,
    render: (_, field) => (
      <Space>
        <Form.Item name={[field.name, 'discount']}><InputNumber /></Form.Item>
        <Form.Item name={[field.name, 'discountType']}><Select options={getDiscountTypeOptions(currency)} /></Form.Item>
      </Space>
    ),
  }, {
    dataIndex: 'amount',
    title: 'Amount',
    sorter: false,
    render: (_, field) => <Form.Item name={[field.name, 'amount']}><InputNumber disabled /></Form.Item>,
  }], [currency]);

  const discountColumns = useMemo(() => [{
    dataIndex: 'couponId',
    title: 'Coupon',
    sorter: false,
    render: (_, field) => (
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item name={[field.name, 'couponId']}><Select options={coupons} allowClear loading={loading} /></Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={[field.name, 'title']}><Input /></Form.Item>
        </Col>
      </Row>
    ),
  }, {
    dataIndex: 'description',
    title: 'Description',
    sorter: false,
    render: (_, field) => <Form.Item name={[field.name, 'description']}><Input /></Form.Item>,
  }, {
    dataIndex: 'discount',
    title: 'Discount',
    sorter: false,
    width: 150,
    render: (_, field) => (
      <Space>
        <Form.Item name={[field.name, 'amount']}><InputNumber /></Form.Item>
        <Form.Item name={[field.name, 'type']}><Select options={getDiscountTypeOptions(currency)} /></Form.Item>
      </Space>
    ),
  }, {
    dataIndex: 'calculatedAmount',
    title: 'Amount',
    sorter: false,
    render: (_, field) => <Form.Item name={[field.name, 'calculatedAmount']}><InputNumber disabled /></Form.Item>,
  }], [currency, coupons]);

  const onValuesChange = (changedValues, allValues) => {
    const calculatedOrder = getCalculatedOrder(allValues);
    if (changedValues.items) {
      for (const [key, item] of changedValues.items.entries()) {
        if (item?.relationId) {
          const product = data?.activeModuleLicenses.find(l => l.id === item.relationId);
          if (product && !form.getFieldValue(['items', key, 'title'])) {
            form.setFieldValue(['items', key, 'title'], product.uniqueTitle || product.title);
          }
        }
        const amount = calculatedOrder.items[key]?.amount;
        if (amount != null) {
          form.setFieldValue(['items', key, 'amount'], amount);
        }
      }
    }
    if (changedValues.discounts) {
      for (const [key, discount] of changedValues.discounts.entries()) {
        if (discount?.couponId) {
          const coupon = data?.activeCoupons.find(c => c.id === discount.couponId);
          if (coupon) {
            if (!form.getFieldValue(['discounts', key, 'title'])) {
              form.setFieldValue(['discounts', key, 'title'], `Coupon: ${coupon.code}`);
            }
            if (!form.getFieldValue(['discounts', key, 'description'])) {
              form.setFieldValue(['discounts', key, 'description'], coupon.description);
            }
            form.setFieldValue(['discounts', key, 'amount'], coupon.discountAmount);
            form.setFieldValue(['discounts', key, 'type'], coupon.discountType);
          }
        }
      }
    }
    const calculatedDiscounts = calculatedOrder.discounts || [];
    calculatedDiscounts.forEach((discount, index) => {
      if (discount.calculatedAmount != null) {
        form.setFieldValue(['discounts', index, 'calculatedAmount'], discount.calculatedAmount);
      }
    });
    if (changedValues.subtotal == null) {
      form.setFieldValue('subtotal', calculatedOrder.subtotal);
    }
    if (changedValues.amount == null) {
      form.setFieldValue('amount', calculatedOrder.amount);
    }
  };

  return (
    <Form form={form} onFinish={onSubmit} onValuesChange={onValuesChange} style={{ marginTop: 16 }} layout="vertical">
      <Form.Item label="Items">
        <Form.List name="items">
          {(fields, { add, remove, move }) => (
            <Table
              columns={lineItemColumns}
              rowKey="key"
              dataSource={fields}
              pagination={false}
              sortable
              onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
              actions={field => (
                field.isListField && <RemoveInput onRemove={() => remove(field.name)} />
              )}
              footerActions={(
                <Button onClick={() => add({})} icon={<PlusOutlined />}>Add row</Button>
              )}
            />
          )}
        </Form.List>
      </Form.Item>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Form.Item name="subtotal">
          <AmountField label="Subtotal" currency={currency} />
        </Form.Item>
      </div>

      <Form.Item label="Discounts">
        <Form.List name="discounts">
          {(fields, { add, remove, move }) => (
            <Table
              columns={discountColumns}
              rowKey="key"
              dataSource={fields}
              pagination={false}
              sortable
              onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
              actions={field => (
                field.isListField && <RemoveInput onRemove={() => remove(field.name)} />
              )}
              footerActions={(
                <Button onClick={() => add({ type: DiscountType.Amount })} icon={<PlusOutlined />}>Add row</Button>
              )}
            />
          )}
        </Form.List>
      </Form.Item>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Form.Item name="amount">
          <AmountField label="Total" currency={currency} />
        </Form.Item>
      </div>
    </Form>
  );
};

export default OrderForm;
